import { Clock } from '@creditornot/cb-icons';
import { Tag } from '@creditornot/cb-tag';
import R from 'ramda';
import React from 'react';
import { Field, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { WeeklyScheduleInput } from 'common/components/weekly-schedules';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { useRowOverrideContext } from './WithOverrideToggle';
import { BaseField } from './types';
import { useOfferOverridesContext } from '../../../context/OfferOverridesContext';
import { FormRowRenderer } from '../FormRowRenderer';
import { NewWeeklyScheduleView } from '../NewWeeklyScheduleView';
import {
  ColumnContainer,
  PreviewRowContainer,
  RadioWrapper,
  StyledRadio,
  WeeklyScheduleWrapper,
} from '../OfferDetailsFormFields.styles';
import { AvailabilityDetails, FormValues } from '../types';

const StyledTag = styled(Tag).attrs({ variant: 'lightBlack' })`
  display: inline-flex;
  align-items: baseline;
`;

const StyledClock = styled(Clock).attrs({ width: 12, height: 12 })`
  margin-inline-end: 4px;
`;

const ScheduleTable = styled.section`
  display: table;

  tr > td {
    padding-block-end: 12px;

    &:last-child {
      padding-block-end: unset;
    }
  }

  tr > td:first-child {
    padding-inline-end: 8px;
  }
`;

export const ItemVisibility = ({ sidePanelMode, offer }: BaseField) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const intl = useIntl();

  const { values } = useFormState<FormValues>();
  const { isOverrideToggleOn, isRowGroupEnabledForOverride } = useRowOverrideContext();
  const { maybeOverriddenOffer, getOfferAttrHasOverride } = useOfferOverridesContext();

  const isVisibilityOverridden = getOfferAttrHasOverride('weekly_visibility');

  const useWeeklyVisibilityFieldNameToUse = isOverrideToggleOn ? 'useWeeklyVisibilityOverride' : 'useWeeklyVisibility';
  const useWeeklyVisibilityValueToUse = values[useWeeklyVisibilityFieldNameToUse];
  const weeklyVisibilityFieldNameToUse = isOverrideToggleOn ? 'weeklyVisibilityOverride' : 'weeklyVisibility';

  const previewComponent = (
    <PreviewRowContainer>
      {!maybeOverriddenOffer?.weekly_visibility?.length && (
        <ScheduleTable>
          <tr>
            <td>
              <FormattedMessage id="special.availability-during-label" />
            </td>
            <td>
              <StyledTag>
                <StyledClock />
                <FormattedMessage id="special.availability-opening-hours-label" />
              </StyledTag>
            </td>
          </tr>
        </ScheduleTable>
      )}
      {maybeOverriddenOffer?.weekly_visibility && maybeOverriddenOffer?.weekly_visibility?.length > 0 && (
        <NewWeeklyScheduleView weeklySchedule={maybeOverriddenOffer.weekly_visibility} />
      )}
    </PreviewRowContainer>
  );

  const editComponent = (
    <ColumnContainer>
      <Field<AvailabilityDetails['useWeeklyVisibility']> name={useWeeklyVisibilityFieldNameToUse}>
        {({ input }) => (
          <RadioWrapper>
            <StyledRadio checked={!input.value} onChange={() => input.onChange(!input.value)}>
              <FormattedMessage id="menu-item.time-visible-always" />
            </StyledRadio>
            <StyledRadio checked={input.value} onChange={() => input.onChange(!input.value)}>
              <FormattedMessage id="menu-item.time-visible-sometimes" />
            </StyledRadio>
          </RadioWrapper>
        )}
      </Field>

      {useWeeklyVisibilityValueToUse && (
        <Field<AvailabilityDetails['weeklyVisibility']> name={weeklyVisibilityFieldNameToUse} isEqual={R.equals}>
          {({ input, meta }) => (
            <WeeklyScheduleWrapper>
              <WeeklyScheduleInput
                onChange={input.onChange}
                pristine={!!meta.pristine}
                select24HoursLabel={intl.formatMessage({ id: 'weeklyScheduleInput.select24Hours' })}
                value={input.value}
              />
            </WeeklyScheduleWrapper>
          )}
        </Field>
      )}
    </ColumnContainer>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'menu-item.time-visible-customers' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      sidePanelMode={sidePanelMode}
      isOverridden={isVisibilityOverridden}
      canBeOverridden={isRowGroupEnabledForOverride}
      offer={offer}
    />
  );
};
